import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const galleryImages = [
  {
    src: '/photos/IMG_6245.JPG',
    alt: 'Kayaking on the lake',
  },
  {
    src: '/photos/IMG_1177.JPG',
    alt: 'Evening by the pool',
  },
  {
    src: '/photos/IMG_7187.JPG',
    alt: 'Costume party fun',
  },
  {
    src: '/photos/IMG_engagement.JPG',
    alt: 'Sailing together',
  },
  {
    src: '/photos/IMG_0137.jpeg',
    alt: 'Biking by the canal',
  },
  {
    src: '/photos/IMG_6684.jpeg',
    alt: 'Cathedral visit',
  },
  {
    src: '/photos/IMG_8964.jpeg',
    alt: 'Kitesurfing beach',
  },
  {
    src: '/photos/PHOTO_2025-05-05.jpg',
    alt: 'Castle visit',
  },
  {
    src: '/photos/IMG_1288.JPG',
    alt: 'Morocco adventure',
  },
  {
    src: '/photos/IMG_0669.jpeg',
    alt: 'Art gallery mirror',
  },
  {
    src: '/photos/IMG_9800.jpeg',
    alt: 'Sailing selfie',
  },
  {
    src: '/photos/IMG_3807.JPG',
    alt: 'Countryside hike',
  },
];

export const GalleryPage = () => {
  const { t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const overlayRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    if (selectedImage !== null) {
      overlayRef.current?.focus();
    }
  }, [selectedImage]);

  useEffect(() => {
    if (selectedImage === null) return;

    const overlay = overlayRef.current;
    if (!overlay) return;

    const focusableSelectors = 'button:not([disabled])';
    const focusableElements = Array.from(overlay.querySelectorAll(focusableSelectors));

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === overlay) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    overlay.addEventListener('keydown', handleKeyDown);
    return () => overlay.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  const openLightbox = (index) => {
    openerRef.current = document.activeElement;
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    openerRef.current?.focus();
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setSelectedImage((prev) => (prev + 1) % galleryImages.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goToPrevious(e);
    if (e.key === 'ArrowRight') goToNext(e);
  };

  return (
    <section className="pt-14 pb-24 bg-[#FAFAFA] min-h-screen" data-testid="gallery-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2A26] mb-4">
            {t('galleryTitle')}
          </h1>
          <div className="w-12 h-0.5 bg-[#B5985A] mx-auto" />
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {galleryImages.map((image, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="aspect-square overflow-hidden rounded-sm cursor-pointer"
              onClick={() => openLightbox(index)}
              aria-label={image.alt}
              data-testid={`gallery-image-${index}`}
            >
              <img
                src={image.src}
                alt=""
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage !== null && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={t('lightboxLabel')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
            onClick={closeLightbox}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            ref={overlayRef}
            data-testid="lightbox"
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors z-10"
              aria-label={t('lightboxClose')}
            >
              <X className="w-8 h-8" />
            </button>

            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10"
              aria-label={t('lightboxPrevious')}
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>

            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors z-10"
              aria-label={t('lightboxNext')}
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            {/* Image */}
            <motion.img
              key={selectedImage}
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].alt}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            />

            {/* Image Counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              {selectedImage + 1} / {galleryImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
